import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Car,
  Wrench,
  Shield,
  Phone,
  MessageSquare,
  MapPin,
  Loader2,
  Droplets,
  Zap,
  Calendar,
  Instagram,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import StickyMobileBar from "@/components/StickyMobileBar";
import { api, BUSINESS } from "@/lib/config";

const SERVICE_ICONS = {
  exterior_detail: Droplets,
  interior_detail: Sparkles,
  full_detail: Wrench,
  ceramic_coating: Shield,
  paint_correction: Zap,
};

const VEHICLE_LABEL = { car: "Car", suv: "SUV", truck: "Truck" };

const TIME_SLOTS = [
  { id: "morning", label: "Morning", sub: "8am – 12pm" },
  { id: "afternoon", label: "Afternoon", sub: "12pm – 4pm" },
  { id: "evening", label: "Evening", sub: "4pm – 7pm" },
];

const todayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const inputCls =
  "w-full h-12 px-4 rounded-xl bg-black border border-zinc-800 text-white placeholder:text-zinc-600 focus:border-otgg-red focus:outline-none focus:ring-1 focus:ring-otgg-red transition-colors";

export default function BookingPage() {
  const [params] = useSearchParams();
  const initialService = params.get("service") || "exterior_detail";

  const [services, setServices] = useState([]);
  const [protection, setProtection] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    service_type: initialService,
    vehicle_type: "car",
    add_ons: [],
    preferred_date: todayISO(),
    preferred_time: "morning",
    name: "",
    email: "",
    phone: "",
    street_address: "",
    city: "",
    state: "NH",
    zip_code: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data.services || []);
      setProtection(r.data.protection || []);
      setAddOns(r.data.add_ons || []);
    }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAddOn = (id) => {
    setForm((f) => ({
      ...f,
      add_ons: f.add_ons.includes(id) ? f.add_ons.filter((x) => x !== id) : [...f.add_ons, id],
    }));
  };

  const allServices = [...services, ...protection];
  const currentService = allServices.find((s) => s.id === form.service_type);

  const price = useMemo(() => {
    if (!currentService?.starting_at) return null;
    return currentService.starting_at[form.vehicle_type] ?? null;
  }, [currentService, form.vehicle_type]);

  const validStep1 = !!form.service_type && !!form.vehicle_type;
  const validStep2 = !!form.preferred_date && !!form.preferred_time;
  const validStep3 =
    form.name.trim().length >= 2 &&
    form.phone.trim().length >= 7 &&
    form.street_address.trim() &&
    form.city.trim() &&
    form.state.trim() &&
    form.zip_code.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.vehicle_year) delete payload.vehicle_year;
      if (!payload.vehicle_make) delete payload.vehicle_make;
      if (!payload.vehicle_model) delete payload.vehicle_model;
      if (!payload.notes) delete payload.notes;
      const res = await api.post("/bookings", payload);
      setSubmitted(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail
        ? typeof e.response.data.detail === "string"
          ? e.response.data.detail
          : "Please double-check your details."
        : "Couldn't submit booking. Try again or call us.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ SUCCESS SCREEN ============
  if (submitted) {
    return (
      <div className="bg-black min-h-screen text-zinc-200">
        <Nav />
        <section data-testid="booking-success" className="pt-32 md:pt-40 pb-24 px-5 md:px-10 max-w-2xl mx-auto text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-otgg-red text-white flex items-center justify-center mb-6">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-light text-white mb-4">
            You're booked.
          </h1>
          <p className="text-zinc-400 max-w-md mx-auto leading-relaxed mb-8">
            Thanks {submitted.name.split(" ")[0]} — we'll text or call <span className="text-white font-semibold">{submitted.phone}</span> shortly to confirm.
          </p>
          <div className="inline-block rounded-xl border border-zinc-800 bg-zinc-950 px-6 py-4 mb-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Booking Reference</div>
            <div data-testid="booking-id" className="font-mono text-white text-sm">{submitted.id.slice(0, 8).toUpperCase()}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-otgg-red text-white font-semibold hover:bg-otgg-red transition-colors"
            >
              <Phone size={14} /> Call {BUSINESS.phone}
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors"
            >
              Back to home
            </Link>
          </div>
          <a
            href={BUSINESS.instagramUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="success-instagram"
            className="mt-10 inline-flex items-center gap-2 text-zinc-400 hover:text-otgg-red transition-colors text-sm"
          >
            <Instagram size={14} /> Follow us @{BUSINESS.instagram}
          </a>

          {/* Google review nudge — happy customers who already booked us to leave a 5-star review */}
          <div className="mt-10 max-w-md mx-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-5 text-left">
            <div className="flex items-start gap-3">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="shrink-0 mt-0.5">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <div className="flex-1">
                <div className="font-heading text-base text-white leading-tight">Love it after we're done?</div>
                <p className="text-zinc-400 text-xs mt-1 leading-relaxed">
                  A quick Google review keeps this thing rolling for us. Takes 30 seconds — huge for a small business.
                </p>
                <a
                  href={BUSINESS.googleReviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="success-google-review"
                  className="inline-flex items-center gap-1.5 mt-3 text-otgg-red text-sm font-semibold hover:gap-2 transition-all"
                >
                  Leave a Google review <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const stepLabels = ["Service", "Schedule", "Contact"];

  return (
    <div className="bg-black min-h-screen text-zinc-200">
      <Nav />
      <section data-testid="booking-page" className="pt-24 md:pt-32 pb-40 lg:pb-24 px-4 md:px-10 max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="text-xs uppercase tracking-[0.25em] text-otgg-red mb-3">Book in 3 taps</div>
        <h1 className="font-heading text-3xl md:text-5xl tracking-tighter font-light text-white mb-8 md:mb-10">
          Let's get you booked.
        </h1>

        {/* Stepper */}
        <ol data-testid="step-indicator" className="flex items-center gap-2 md:gap-3 mb-8 md:mb-10">
          {stepLabels.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <li key={label} className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <span
                    className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold border-2 transition-colors ${
                      active
                        ? "bg-otgg-red text-white border-otgg-red"
                        : done
                        ? "bg-otgg-red/20 text-otgg-red border-otgg-red/40"
                        : "bg-transparent text-zinc-600 border-zinc-800"
                    }`}
                  >
                    {done ? <CheckCircle2 size={14} /> : n}
                  </span>
                  <span className={`text-xs md:text-sm ${active ? "text-white font-semibold" : "text-zinc-500"} hidden sm:inline`}>
                    {label}
                  </span>
                </div>
                {n < stepLabels.length && <span className={`flex-1 h-px ${done ? "bg-otgg-red/40" : "bg-zinc-800"}`} />}
              </li>
            );
          })}
        </ol>

        {/* ============ STEP 1: SERVICE + VEHICLE ============ */}
        {step === 1 && (
          <div data-testid="step-service" className="space-y-8">
            {/* Service */}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">Pick a service</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {services.filter((s) => s.id !== "maintenance_clean").map((s) => {
                  const Icon = SERVICE_ICONS[s.id] || Car;
                  const active = form.service_type === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => update("service_type", s.id)}
                      data-testid={`select-service-${s.id}`}
                      className={`text-left rounded-2xl border p-4 md:p-5 transition-all active:scale-[0.98] ${
                        active
                          ? "border-otgg-red bg-otgg-red/10"
                          : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                      }`}
                    >
                      <Icon size={22} className={active ? "text-otgg-red" : "text-zinc-400"} />
                      <div className="font-heading text-lg text-white mt-3">{s.name}</div>
                    </button>
                  );
                })}
              </div>
              {protection.length > 0 && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {protection.map((s) => {
                    const Icon = SERVICE_ICONS[s.id] || Shield;
                    const active = form.service_type === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => update("service_type", s.id)}
                        data-testid={`select-service-${s.id}`}
                        className={`text-left rounded-2xl border p-4 md:p-5 transition-all active:scale-[0.98] flex items-center gap-3 ${
                          active
                            ? "border-otgg-red bg-otgg-red/10"
                            : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                        }`}
                      >
                        <Icon size={22} className={active ? "text-otgg-red" : "text-zinc-400"} />
                        <div className="font-heading text-base text-white">{s.name}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Vehicle type */}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">Vehicle size</label>
              <div className="grid grid-cols-3 gap-2.5">
                {["car", "suv", "truck"].map((v) => {
                  const active = form.vehicle_type === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => update("vehicle_type", v)}
                      data-testid={`vehicle-type-${v}`}
                      className={`rounded-2xl border p-4 md:p-5 transition-all active:scale-[0.98] ${
                        active
                          ? "border-otgg-red bg-otgg-red/10"
                          : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                      }`}
                    >
                      <Car size={22} className={`mx-auto ${active ? "text-otgg-red" : "text-zinc-400"}`} />
                      <div className="text-white text-sm font-semibold mt-2">{VEHICLE_LABEL[v]}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price banner */}
            {price != null && (
              <div data-testid="price-banner" className="rounded-2xl border border-otgg-red/30 bg-gradient-to-br from-otgg-red/10 to-black p-5 md:p-6 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-otgg-red mb-1">Starting at</div>
                  <div className="text-white text-sm">{currentService?.name} · {VEHICLE_LABEL[form.vehicle_type]}</div>
                </div>
                <div className="font-heading text-4xl md:text-5xl text-white tabular-nums">${price}</div>
              </div>
            )}

            {/* Add-ons */}
            {addOns.length > 0 && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                  Add-ons <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {addOns.map((a) => {
                    const checked = form.add_ons.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAddOn(a.id)}
                        data-testid={`addon-${a.id}`}
                        className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-all active:scale-[0.98] ${
                          checked
                            ? "border-otgg-red bg-otgg-red/10"
                            : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-5 h-5 rounded-md border-2 grid place-items-center shrink-0 ${checked ? "bg-otgg-red border-otgg-red" : "border-zinc-700"}`}>
                            {checked && <CheckCircle2 size={12} className="text-white" />}
                          </span>
                          <span className="text-white text-sm text-left truncate">{a.name}</span>
                        </div>
                        <span className="text-otgg-red font-heading text-base tabular-nums ml-2">+${a.price_from}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="button"
              disabled={!validStep1}
              onClick={() => setStep(2)}
              data-testid="step1-next"
              className="w-full h-14 rounded-2xl bg-otgg-red text-white font-bold text-base hover:bg-otgg-red disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ============ STEP 2: SCHEDULE ============ */}
        {step === 2 && (
          <div data-testid="step-schedule" className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block flex items-center gap-2">
                <Calendar size={12} /> Preferred date
              </label>
              <input
                type="date"
                min={todayISO()}
                value={form.preferred_date}
                onChange={(e) => update("preferred_date", e.target.value)}
                className={inputCls}
                data-testid="input-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">Preferred time</label>
              <div className="grid grid-cols-3 gap-2.5">
                {TIME_SLOTS.map((t) => {
                  const active = form.preferred_time === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => update("preferred_time", t.id)}
                      data-testid={`time-${t.id}`}
                      className={`rounded-2xl border p-4 text-left transition-all active:scale-[0.98] ${
                        active
                          ? "border-otgg-red bg-otgg-red/10"
                          : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                      }`}
                    >
                      <div className="text-white text-sm font-semibold">{t.label}</div>
                      <div className="text-zinc-500 text-xs mt-0.5">{t.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <details className="rounded-xl border border-zinc-800 bg-zinc-950">
              <summary className="cursor-pointer px-4 py-3 text-sm text-zinc-300 select-none">
                Vehicle details <span className="text-zinc-500 text-xs">(optional — helps us prep)</span>
              </summary>
              <div className="p-4 pt-1 space-y-3">
                <div className="grid grid-cols-3 gap-2.5">
                  <input value={form.vehicle_year} onChange={(e) => update("vehicle_year", e.target.value)} placeholder="Year" className={inputCls} data-testid="input-vehicle-year" />
                  <input value={form.vehicle_make} onChange={(e) => update("vehicle_make", e.target.value)} placeholder="Make" className={inputCls} data-testid="input-vehicle-make" />
                  <input value={form.vehicle_model} onChange={(e) => update("vehicle_model", e.target.value)} placeholder="Model" className={inputCls} data-testid="input-vehicle-model" />
                </div>
                <textarea
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  rows={2}
                  placeholder="Anything we should know? (pet hair, fresh mud, etc.)"
                  className={`${inputCls} h-auto py-3 resize-none`}
                  data-testid="input-notes"
                />
              </div>
            </details>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="step2-back"
                className="h-14 px-6 rounded-2xl border border-zinc-800 text-zinc-300 hover:border-zinc-600 transition-colors inline-flex items-center gap-2 active:scale-[0.98]"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                type="button"
                disabled={!validStep2}
                onClick={() => setStep(3)}
                data-testid="step2-next"
                className="flex-1 h-14 rounded-2xl bg-otgg-red text-white font-bold text-base hover:bg-otgg-red disabled:opacity-30 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 3: CONTACT ============ */}
        {step === 3 && (
          <div data-testid="step-contact" className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Your name</label>
              <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" className={inputCls} data-testid="input-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Phone <span className="text-otgg-red">*</span></label>
              <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className={inputCls} data-testid="input-phone" />
              <p className="text-xs text-zinc-500 mt-1.5">We'll text you to confirm.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Email <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></label>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className={inputCls} data-testid="input-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Service address</label>
              <input value={form.street_address} onChange={(e) => update("street_address", e.target.value)} placeholder="123 Main St" className={inputCls} data-testid="input-address" />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              <input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="City" className={inputCls} data-testid="input-city" />
              <input value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="State" className={inputCls} data-testid="input-state" />
              <input value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)} placeholder="ZIP" className={inputCls} data-testid="input-zip" />
            </div>

            {/* Summary */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5 mt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Summary</div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-zinc-500">Service</dt><dd className="text-white text-right">{currentService?.name || "—"}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-zinc-500">Vehicle</dt><dd className="text-white">{VEHICLE_LABEL[form.vehicle_type]}</dd></div>
                {price != null && (
                  <div className="flex justify-between gap-4"><dt className="text-zinc-500">Starting at</dt><dd className="text-otgg-red font-heading text-lg tabular-nums">${price}</dd></div>
                )}
                {form.add_ons.length > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-500">Add-ons</dt>
                    <dd className="text-white text-right text-xs">{form.add_ons.map((id) => addOns.find((a) => a.id === id)?.name).filter(Boolean).join(", ")}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4"><dt className="text-zinc-500">When</dt><dd className="text-white text-right">{form.preferred_date} · <span className="capitalize">{form.preferred_time}</span></dd></div>
              </dl>
              <p className="text-xs text-zinc-500 mt-4 border-t border-zinc-900 pt-3">
                No payment required now. We'll confirm by call or text, then take payment in person after the job.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStep(2)}
                data-testid="step3-back"
                className="h-14 px-6 rounded-2xl border border-zinc-800 text-zinc-300 hover:border-zinc-600 transition-colors inline-flex items-center gap-2 active:scale-[0.98]"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                type="button"
                disabled={!validStep3 || submitting}
                onClick={submit}
                data-testid="submit-booking-btn"
                className="flex-1 h-14 rounded-2xl bg-otgg-red text-white font-bold text-base hover:bg-otgg-red disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Booking…</>
                ) : (
                  <>Confirm Booking <ArrowRight size={16} /></>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-zinc-900 flex flex-wrap gap-x-6 gap-y-3 items-center text-sm text-zinc-500">
          <span className="inline-flex items-center gap-2"><MapPin size={14} /> {BUSINESS.serviceArea}</span>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={14}/> {BUSINESS.phone}</a>
          <a href={BUSINESS.smsHref} className="inline-flex items-center gap-2 hover:text-white transition-colors"><MessageSquare size={14}/> Text us</a>
        </div>
      </section>
      <Footer />
      <StickyMobileBar />
    </div>
  );
}
